import { motion } from "framer-motion";
import { Instagram, Mail, MessageCircle, ArrowUpRight, FolderOpen } from "lucide-react";

const SOCIALS = [
  {
    label: "Instagram",
    handle: "@lbasitl",
    href: "https://www.instagram.com/lbasitl/",
    icon: Instagram,
    testid: "contact-instagram",
  },
  {
    label: "Behance",
    handle: "/basitloay",
    href: "https://www.behance.net/basitloay",
    icon: ArrowUpRight,
    testid: "contact-behance",
  },
  {
    label: "أعمالي على Google Drive",
    handle: "View portfolio drive",
    href: "https://drive.google.com/drive/folders/10OyzYMppUh3xTAaos5bXePrd4iNQYx7l?usp=drive_link",
    icon: FolderOpen,
    testid: "contact-drive",
  },
  {
    label: "WhatsApp",
    handle: "+964 782 557 9755",
    href: "https://wa.me/9647825579755",
    icon: MessageCircle,
    testid: "contact-whatsapp",
  },
  {
    label: "Email",
    handle: "basitloay7@gmail.com",
    href: "mailto:basitloay7@gmail.com",
    icon: Mail,
    testid: "contact-email",
  },
];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-32 md:py-48 overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#FD1843]/8 blur-[180px]" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="flex items-center mb-14"
        >
          <span className="h-rule" />
          <span className="overline">06 — Get in touch</span>
        </motion.div>

        {/* Arabic heading */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1 }}
          dir="rtl"
          className="font-arabic font-bold text-white tracking-tight leading-[1.15] text-[10vw] md:text-[7vw] lg:text-[6vw] max-w-[1200px]"
        >
          خلّينا نصنع شيئ <span className="text-[#FD1843] glow-text">استثنائي</span>
        </motion.h2>

        {/* English heading below */}
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-display font-semibold text-white/50 uppercase tracking-[-0.03em] leading-none text-[7vw] md:text-[4vw] lg:text-[3vw] mt-4"
        >
          Let's Build Something Exceptional.
        </motion.h3>

        {/* Arabic paragraph */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, delay: 0.3 }}
          dir="rtl"
          className="font-arabic text-white/80 text-lg md:text-xl font-medium leading-[1.95] max-w-2xl mt-10"
        >
          عندك مشروع، براند، أو استفسار عن الدورة؟ تواصل وياي واختر الطريقة
          الأنسب إلك، وأرد عليك بأقرب وقت ممكن.
        </motion.p>

        {/* English paragraph */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-white/50 text-base md:text-lg font-light max-w-2xl mt-4 leading-relaxed"
        >
          Have a project, brand, or course inquiry? Choose the contact method
          that works best for you and I'll get back to you as soon as possible.
        </motion.p>

        {/* Contact cards */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {SOCIALS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (s.href.startsWith("mailto:")) return;
                  e.preventDefault();
                  const w = window.open(s.href, "_blank", "noopener,noreferrer");
                  if (!w) {
                    try { window.top.location.href = s.href; }
                    catch { window.location.href = s.href; }
                  }
                }}
                data-testid={s.testid}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.8, delay: i * 0.07 }}
                className="group relative flex items-center justify-between border border-white/10 hover:border-[#FD1843]/60 bg-white/[0.02] hover:bg-[#FD1843]/5 rounded-sm p-7 md:p-9 transition-all duration-500"
                data-magnetic
              >
                <div className="space-y-2">
                  <div className="font-mono text-[10px] tracking-[0.28em] text-[#FD1843] uppercase">
                    {s.label}
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
                    {s.handle}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Icon
                    className="text-white/40 group-hover:text-[#FD1843] transition-colors"
                    size={22}
                  />
                  <div className="w-12 h-12 rounded-full border border-white/10 grid place-items-center group-hover:bg-[#FD1843] group-hover:border-[#FD1843] group-hover:text-[#171717] transition-all duration-500">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
